import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { 
  Users, 
  Video,
  Phone, 
  Mail, 
  MapPin,
  ChevronDown,
  Zap,
  Calendar
} from "lucide-react";
import BookingPage from "./pages/Booking";

// Baseball Icon Component
const BaseballIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M4.93 4.93c4.08 4.08 4.08 10.06 0 14.14" />
    <path d="M19.07 4.93c-4.08 4.08-4.08 10.06 0 14.14" />
  </svg>
);

// Service data
const services = [
  {
    id: 1,
    title: "PRIVATE LESSONS",
    description: "This training is fully personalized for your player. We break down what they do well, identify what needs work, and create a targeted plan to help them improve faster. Players receive fundamental training in every aspect of the game, plus a customized plan to continue developing outside of lessons.",
    icon: BaseballIcon,
  },
  {
    id: 2,
    title: "GROUP TRAINING",
    description: "This is a great opportunity for siblings, friends, or teammates to train together in a competitive environment where they can learn fundamentals, work through game situations, and develop a higher baseball IQ.",
    icon: Users,
  },
  {
    id: 3,
    title: "REMOTE TRAINING",
    description: "Get expert coaching from anywhere with video analysis and personalized feedback designed to fit your schedule. Each player receives a customized development plan, starting with a specific warm-up and progressing through tee work and cage BP. We also provide an in-game approach and a clear plan at the plate that players can apply immediately.",
    icon: Video,
  },
  {
    id: 4,
    title: "SPEED & AGILITY",
    description: "Coming soon.",
    icon: Zap,
  },
];

// Personal photos for decoration
const personalPhotos = [
  "https://customer-assets.emergentagent.com/job_slugger-lessons/artifacts/m31hlxug_0d2f65b2-cf79-401b-9ff0-27148a13616a.jpg",
  "https://customer-assets.emergentagent.com/job_slugger-lessons/artifacts/tx7g7oit_4cde60fe-8cd8-4ba7-90b3-42b33af7ad9d.jpg",
  "https://customer-assets.emergentagent.com/job_slugger-lessons/artifacts/7fwkd8w7_e1d4ae6b-b3a3-4a8d-b3dc-0c9e4ff9e42b.jpg",
  "https://customer-assets.emergentagent.com/job_slugger-lessons/artifacts/s1ihqc5n_78525054-ae55-4302-80a9-38c353b1d907.jpg",
  "https://customer-assets.emergentagent.com/job_slugger-lessons/artifacts/y0x7m7nc_b3897abc-c5f6-416d-abd8-9e93eae63ee0.png",
];

// Gallery images using personal photos
const galleryImages = [
  {
    id: 1,
    url: personalPhotos[0],
    alt: "Baseball player in action",
    isPlaceholder: false,
    spanClass: "span-2-col",
  },
  {
    id: 2,
    url: personalPhotos[1],
    alt: "Team celebration",
    isPlaceholder: false,
    spanClass: "",
  },
  {
    id: 3,
    url: personalPhotos[2],
    alt: "Award ceremony",
    isPlaceholder: false,
    spanClass: "",
  },
  {
    id: 4,
    url: personalPhotos[3],
    alt: "Championship team",
    isPlaceholder: false,
    spanClass: "span-2-row",
  },
  {
    id: 5,
    url: personalPhotos[4],
    alt: "Night game action",
    isPlaceholder: false,
    spanClass: "span-2-col",
  },
];

// Navigation Component
const Navigation = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-16">
          <button 
            onClick={() => scrollToSection("hero")}
            className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight text-slate-900 hover:text-red-600 transition-colors"
            data-testid="nav-logo"
          >
            CRIPPS BASEBALL
          </button>
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection("about")}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors uppercase tracking-wider"
              data-testid="nav-about"
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection("services")}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors uppercase tracking-wider"
              data-testid="nav-services"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection("gallery")}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors uppercase tracking-wider"
              data-testid="nav-gallery"
            >
              Gallery
            </button>
            <Link
              to="/book"
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors uppercase tracking-wider flex items-center gap-1.5"
              data-testid="nav-book"
            >
              <Calendar size={14} />
              Book
            </Link>
            <button 
              onClick={() => scrollToSection("contact")}
              className="bg-slate-900 text-white px-6 py-2 text-sm font-bold uppercase tracking-wider hover:bg-red-600 transition-colors"
              data-testid="nav-contact-btn"
            >
              Contact
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <section id="hero" className="min-h-screen relative flex items-center" data-testid="hero-section">
      {/* Background photo collage */}
      <div className="absolute inset-0 grid grid-cols-2">
        <div 
          className="bg-cover bg-center"
          style={{ backgroundImage: `url(${personalPhotos[0]})` }}
        />
        <div 
          className="bg-cover bg-center"
          style={{ backgroundImage: `url(https://customer-assets.emergentagent.com/job_slugger-lessons/artifacts/cci2w0rd_0d6120fb-4266-4acc-a56e-b052e0786452.png)` }}
        />
      </div>
      <div className="absolute inset-0 bg-white/60" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-left">
        <p className="text-sm font-medium tracking-widest uppercase text-slate-500 mb-4 animate-fade-in-up">
          Train With An Expert
        </p>
        <h1 className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-black tracking-tighter uppercase text-slate-900 mb-6 animate-fade-in-up animate-delay-100">
          CRIPPS<br />BASEBALL<br />TRAINING
        </h1>
        <button 
          onClick={() => navigate("/book")}
          className="btn-primary animate-fade-in-up animate-delay-200"
          data-testid="hero-cta-btn"
        >
          Book a Session
        </button>
      </div>
      <button 
        onClick={() => document.getElementById("services").scrollIntoView({ behavior: "smooth" })}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-400 hover:text-slate-900 transition-colors animate-bounce"
        data-testid="scroll-down-btn"
        aria-label="Scroll down"
      >
        <ChevronDown size={32} />
      </button>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const accolades = [
    ".330 career average over 215 collegiate games",
    "2020 CCCAA hits leader",
    "2021 ACBA/Rawlings All-region team",
    "2022 GAC Honorable mention team",
    "2023 GAC Honorable mention team",
    "2024 All-Tournament team",
    "2024 GAC Conference and Tournament Champion",
    "2024 D2 Super Regional Appearance",
    "2025 D2 Regional Appearance",
  ];

  return (
    <section id="about" className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
          {/* Photos Column */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <img 
                src={personalPhotos[2]} 
                alt="Chase Cripps with trophy"
                className="w-full h-64 md:h-80 object-cover object-top"
              />
            </div>
            <img 
              src={personalPhotos[1]} 
              alt="Team celebration"
              className="w-full h-40 md:h-48 object-cover"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_slugger-lessons/artifacts/4ysvkkvb_8a2da744-746e-45c2-917a-459d6f7e8b36.jpg" 
              alt="Chase batting"
              className="w-full h-40 md:h-48 object-cover"
            />
          </div>
          
          {/* Content Column */}
          <div>
            <p className="text-sm font-medium tracking-widest uppercase text-slate-400 mb-4">
              About Me
            </p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-6">
              CHASE CRIPPS
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Hi, my name is Chase Cripps. I have over 200 games of collegiate baseball experience and earned multiple accolades during my playing career. Through seven years of collegiate baseball, I gained extensive knowledge of player development and high-level competition, and I am passionate about passing that knowledge on to the next generation of players.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              I am currently the head coach of the Just Dawgs 13U AAA team and the head coach of the sophomore team at St. Thomas Catholic. I also have two years of one-on-one coaching experience and over 20 years of overall baseball knowledge. I understand what it takes to win and what it takes to help players reach the next level.
            </p>
            
            <div className="bg-slate-50 p-6 md:p-8">
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight uppercase text-slate-900 mb-4">
                Accolades
              </h3>
              <ul className="space-y-2">
                {accolades.map((accolade, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-600 mt-2 flex-shrink-0" />
                    <span className="text-slate-600">{accolade}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section 
      id="services" 
      className="py-20 md:py-32 px-6 md:px-12 lg:px-24 relative overflow-hidden" 
      data-testid="services-section"
      style={{ 
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_slugger-lessons/artifacts/4ysvkkvb_8a2da744-746e-45c2-917a-459d6f7e8b36.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <div className="absolute inset-0 bg-white/70" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="mb-16">
          <p className="text-sm font-medium tracking-widest uppercase text-slate-400 mb-4">
            What We Offer
          </p>
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900">
            TRAINING SERVICES
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {services.map((service, index) => (
            <div 
              key={service.id}
              className={`service-card bg-white/90 backdrop-blur-sm p-8 md:p-10 animate-fade-in-up animate-delay-${(index + 1) * 100}`}
              data-testid={`service-card-${service.id}`}
            >
              <service.icon className="service-icon w-10 h-10 text-slate-900 mb-6 transition-colors" />
              <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-4">
                {service.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  return (
    <section id="gallery" className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-slate-50" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900">
            GALLERY
          </h2>
        </div>
        <div className="gallery-grid">
          {galleryImages.map((image) => (
            <div 
              key={image.id} 
              className={`gallery-item ${image.spanClass}`}
              data-testid={`gallery-item-${image.id}`}
            >
              <img 
                src={image.url} 
                alt={image.alt}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section 
      id="contact" 
      className="py-20 md:py-32 px-6 md:px-12 lg:px-24 relative overflow-hidden" 
      data-testid="contact-section"
      style={{ 
        backgroundImage: `url(${personalPhotos[3]})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-slate-900/80" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid md:grid-cols-2 gap-12 md:gap-24">
          <div>
            <p className="text-sm font-medium tracking-widest uppercase text-slate-500 mb-4">
              Get In Touch
            </p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
              LET&apos;S WORK<br />TOGETHER
            </h2>
            <p className="text-slate-400 leading-relaxed max-w-md">
              Call or text anytime to schedule your lesson or learn about the training I&apos;ll offer.
            </p>
          </div>
          <div className="space-y-8">
            <a 
              href="tel:2086591837" 
              className="flex items-center gap-4 group"
              data-testid="contact-phone"
            >
              <div className="w-12 h-12 bg-slate-800 flex items-center justify-center group-hover:bg-red-600 transition-colors">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wider">Phone</p>
                <p className="text-xl text-white font-medium group-hover:text-red-500 transition-colors">
                  (208) 659-1837
                </p>
              </div>
            </a>
            <a 
              href="mailto:chasecripps08@gmail.com" 
              className="flex items-center gap-4 group"
              data-testid="contact-email"
            >
              <div className="w-12 h-12 bg-slate-800 flex items-center justify-center group-hover:bg-red-600 transition-colors">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wider">Email</p>
                <p className="text-xl text-white font-medium group-hover:text-red-500 transition-colors">
                  chasecripps08@gmail.com
                </p>
              </div>
            </a>
            <div className="flex items-start gap-4" data-testid="contact-location">
              <div className="w-12 h-12 bg-slate-800 flex items-center justify-center flex-shrink-0 mt-1">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wider mb-2">Locations</p>
                <p className="text-lg text-white font-medium">J5 Athletics</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-8 px-6 md:px-12 lg:px-24 bg-slate-950" data-testid="footer">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-slate-500 text-sm">
          © 2024 Cripps Baseball Training. All rights reserved.
        </p>
        <p className="font-['Barlow_Condensed'] text-slate-600 text-sm uppercase tracking-wider">
          Train Hard. Play Harder.
        </p>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  return (
    <div className="min-h-screen">
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <GallerySection />
      <ContactSection />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/book" element={<BookingPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
